import { JSX as LocalJSX } from '@ionic/core';
import { IonBackButton } from '@ionic/react';
import { IonicReactProps } from '@ionic/react/dist/types/components/IonicReactProps';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { HardwareBackButtonListener } from '../button/back-button.listener';
interface BackButtonService {
  getCallbacks: () => BackButtonCallback[];
  register: (cb: BackButtonCallback) => () => void;
}

type BackButtonCallback = () => boolean | Promise<boolean>;

const BackButtonContext = React.createContext<BackButtonService | undefined>(
  undefined
);

interface BackButtonProviderProps {
  children: ReactElement;
}

class BackButtonServiceImpl implements BackButtonService {
  private callbacks: BackButtonCallback[] = [];

  public getCallbacks() {
    return this.callbacks;
  }

  public register(cb: BackButtonCallback) {
    this.callbacks.push(cb);
    return () => {
      this.callbacks = this.callbacks.filter((_cb) => _cb !== cb);
    };
  }
}

export const BackButtonProvider = (
  props: BackButtonProviderProps
): ReactElement => {
  const [backButtonService] = useState<BackButtonService>(
    new BackButtonServiceImpl()
  );

  return (
    <BackButtonContext.Provider value={backButtonService}>
      {props.children}
    </BackButtonContext.Provider>
  );
};

type ButtonProps = Omit<LocalJSX.IonBackButton, 'icon'> &
  IonicReactProps & {
    icon?:
      | {
          ios: string;
          md: string;
        }
      | string;
    ref?: React.RefObject<HTMLIonBackButtonElement>;
    customOnClick?: () => Promise<boolean>;
  };

class CustomBackButtonInternal extends IonBackButton {
  private originalClickButton: (e: React.MouseEvent) => void;

  constructor(props: ButtonProps) {
    super(props);
    this.originalClickButton = this['clickButton'];
    super['clickButton'] = this.customClickButton.bind(this);
  }

  private async customClickButton(e: React.MouseEvent) {
    const { customOnClick } = this.props as ButtonProps;
    const shouldGoBack =
      typeof customOnClick === 'function' ? await customOnClick() : true;
    if (shouldGoBack) {
      this.originalClickButton(e);
    }
  }
}

export const CustomBackButton = (props: ButtonProps): ReactElement => {
  const { defaultHref } = props;

  const backButtonService = useContext(BackButtonContext);

  const customOnClick = async (): Promise<boolean> => {
    const cbs = backButtonService?.getCallbacks();

    if (!cbs || cbs.length === 0) {
      return true;
    }
    const results = await Promise.all(cbs.map((cb) => cb()));
    const goBack = results.findIndex((res) => !res) === -1;
    return goBack;
  };

  return (
    <>
      <HardwareBackButtonListener defaultHref={defaultHref} priority={20} />
      <CustomBackButtonInternal
        className="ion-padding-end"
        defaultHref={defaultHref}
        text=""
        {...{ customOnClick }}
      />
    </>
  );
};

export const useRegisterBackButtonCallback = (cb: BackButtonCallback): void => {
  const backButtonService = useContext(BackButtonContext);

  useEffect(() => {
    const unsubscribeCb = backButtonService?.register(cb);
    return () => {
      if (unsubscribeCb) {
        unsubscribeCb();
      }
    };
  }, [backButtonService, cb]);
};
